import { useState, useEffect } from 'react';
import { savecontactgroup, editcontactgroup } from '../components/DbServices';
import Swal from 'sweetalert2';

export default function AddGroupModal({ userId, contactsList, editData, onClose, onSaved }) {
    const [groupName, setGroupName] = useState('');
    const [selectedIds, setSelectedIds] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (editData) {
            setGroupName(editData.groupname || '');
            try {
                const existingIds = JSON.parse(editData.contacts);
                if (Array.isArray(existingIds)) {
                    setSelectedIds(existingIds.map(id => String(id)));
                }
            } catch (e) {
                console.error("Error parsing existing contacts", e);
                setSelectedIds([]);
            }
        }
    }, [editData]);

    const toggleSelect = (id) => {
        const stringId = String(id);
    
        setSelectedIds(prev => {
            const alreadySelected = prev.includes(stringId);
    
            // If already selected → allow unselect
            if (alreadySelected) {
                return prev.filter(i => i !== stringId);
            }
    
            // If trying to add more than 99 → block
            if (prev.length >= 99) {
                Swal.fire({
                    icon: "warning",
                    title: "Limit Reached",
                    text: "You can only add maximum 99 contacts in one group."
                });
                return prev;
            }
    
            // Otherwise allow selection
            return [...prev, stringId];
        });
    };

    const filteredContacts = contactsList.filter(c => 
        c.name?.toLowerCase().includes(searchTerm.toLowerCase()) || 
        c.number?.includes(searchTerm)
    );

    const handleSubmit = async () => {
        if (!groupName || selectedIds.length === 0) {
            Swal.fire("Please enter a name and select contacts");
            return; 
        }

        setLoading(true);
        const payload = {
            user: userId,
            groupname: groupName,
            contacts: JSON.stringify(selectedIds),
            status: 1
        };

        try {
            if (editData) {
                payload.id = editData.id;
                await editcontactgroup(payload);
            } else {
                await savecontactgroup(payload);
            }
            onSaved();
            onClose();
        } catch (err) {
            Swal.fire("Failed to save group");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="custom-modal-backdrop" style={{ alignItems: 'center' }}>
            <div className="custom-modal border-0 shadow-lg" style={{ maxWidth: '550px', width: '95%', borderRadius: '20px', overflow: 'hidden', padding: '0' }}>
                
                {/* Header with your Gradient Class */}
                <div className="bg-primary p-4 text-white text-center">
                    <h5 className="fw-bold mb-0">
                        {editData ? '✏️ Edit Broadcast Group' : '👥 New Broadcast Group'}
                    </h5>
                    <p className="mb-0 x-small text-white-50">
                        Organize your contacts for bulk messaging
                    </p>
                </div>

                <div className="p-4 bg-white">
                    {/* Group Name Input */}
                    <div className="mb-4">
                        <label className="small fw-bold text-muted mb-2 text-uppercase">Group Identity</label>
                        <input
                            className="form-control form-control-lg border-2 shadow-none"
                            style={{ borderRadius: '12px', fontSize: '15px' }}
                            placeholder="e.g. VIP Customers, New Leads..."
                            value={groupName}
                            onChange={e => setGroupName(e.target.value)}
                        />
                    </div>

                    {/* Member Selection Area */}
                    <div className="d-flex justify-content-between align-items-end mb-2">
                        <label className="small fw-bold text-muted text-uppercase">Select Members</label>
                        <span className="badge bg-primary-subtle text-primary rounded-pill px-3">
                            {selectedIds.length} Members Selected
                        </span>
                    </div>

                    <div className="card border-2 rounded-4 overflow-hidden shadow-none mb-4">
                        {/* Search Bar inside Member List */}
                        <div className="input-group input-group-sm border-bottom p-2 bg-light">
                            <span className="input-group-text bg-transparent border-0"><i className="bi bi-search"></i></span>
                            <input 
                                type="text" 
                                className="form-control bg-transparent border-0 shadow-none" 
                                placeholder="Find a contact..." 
                                value={searchTerm}
                                onChange={e => setSearchTerm(e.target.value)}
                            />
                        </div>

                        <div className="p-2 bg-white" style={{ maxHeight: '250px', overflowY: 'auto' }}>
                            {filteredContacts.length > 0 ? filteredContacts.map(c => {
                                const isChecked = selectedIds.includes(String(c.id));
                                return (
                                    <div
                                        key={c.id}
                                        className={`d-flex align-items-center p-2 mb-1 rounded-3 transition-all ${isChecked ? 'bg-primary bg-opacity-10' : 'bg-light-hover'}`}
                                        onClick={() => toggleSelect(c.id)}
                                        style={{ cursor: 'pointer' }}
                                    >
                                        <div className="form-check mb-0 pointer-none">
                                            <input
                                                className="form-check-input"
                                                type="checkbox"
                                                checked={isChecked}
                                                readOnly
                                            />
                                        </div>
                                        <div className="ms-3">
                                            <div className={`fw-bold small ${isChecked ? 'text-primary' : 'text-dark'}`}>{c.name}</div>
                                            <div className="text-muted" style={{ fontSize: '11px' }}>+{c.number}</div>
                                        </div>
                                    </div>
                                );
                            }) : (
                                <div className="text-center py-4 text-muted x-small">No contacts found</div>
                            )}
                        </div>
                    </div>

                    {/* Actions */}
                    <div className="d-flex gap-2">
                        <button className="btn btn-light w-100 fw-bold py-2 border" style={{ borderRadius: '12px' }} onClick={onClose}>
                            Cancel
                        </button>
                        <button className="btn btn-primary w-100 fw-bold py-2 shadow-sm" style={{ borderRadius: '12px' }} onClick={handleSubmit} disabled={loading}>
                            {loading ? (
                                <><span className="spinner-border spinner-border-sm me-2"></span>Processing...</>
                            ) : (
                                editData ? 'Update Group' : 'Create Group'
                            )}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}